import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ErroApi } from '../lib/api';
import {
  diaDaSemana,
  diaSP,
  horaSP,
  iniciais,
  mesCurto,
  numeroDoDia,
  quandoEmPalavras,
} from '../lib/datas';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import Layout from '../componentes/Layout';

type Pessoa = { nome: string; funcao: string; status: string };

type MinhaEscala = {
  id: number;
  eventoId: number;
  titulo: string;
  local: string | null;
  inicio: string | null;
  funcao: string;
  ministerio: string | null;
  status: 'convocado' | 'confirmado' | 'indisponivel';
  justificativa: string | null;
  observacao: string | null;
  equipe: Pessoa[];
  confirmados: number;
  total: number;
  musicas: number;
};

const CORES_AVATAR = [
  'bg-tinta-400',
  'bg-tinta-900',
  'bg-amber-600',
  'bg-sky-700',
  'bg-violet-700',
  'bg-rose-600',
];

export default function MinhasEscalas() {
  const [quando, setQuando] = useState<'proximas' | 'anteriores'>('proximas');
  const [escalas, setEscalas] = useState<MinhaEscala[]>([]);
  const [semVinculo, setSemVinculo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [respondendo, setRespondendo] = useState<number | null>(null);
  const [justificando, setJustificando] = useState<number | null>(null);
  const [justificativa, setJustificativa] = useState('');

  const carregar = useCallback(async () => {
    try {
      const d = await api<{ escalas: MinhaEscala[]; semVinculo: boolean }>(
        `/minhas-escalas?quando=${quando}`,
      );
      setEscalas(d.escalas);
      setSemVinculo(d.semVinculo);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar suas escalas.');
    }
  }, [quando]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Uma seção por dia, como no aplicativo: dia grande na lateral. */
  const porDia = useMemo(() => {
    const mapa = new Map<string, MinhaEscala[]>();
    for (const e of escalas) {
      if (!e.inicio) continue;
      const dia = diaSP(e.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push(e);
      mapa.set(dia, lista);
    }
    return [...mapa.entries()];
  }, [escalas]);

  async function responder(id: number, status: 'confirmado' | 'indisponivel', texto?: string) {
    setRespondendo(id);
    try {
      await api(`/escalas/${id}/responder`, {
        method: 'POST',
        body: JSON.stringify({ status, ...(texto ? { justificativa: texto } : {}) }),
      });
      setJustificando(null);
      setJustificativa('');
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui responder.');
    } finally {
      setRespondendo(null);
    }
  }

  return (
    <Layout titulo="Minhas escalas" subtitulo="Onde você serve, por data.">
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <div className="mb-5 flex max-w-md rounded-2xl bg-white p-1 shadow-cartao">
        {(
          [
            { valor: 'proximas', rotulo: 'Próximas' },
            { valor: 'anteriores', rotulo: 'Anteriores' },
          ] as const
        ).map((a) => (
          <button
            key={a.valor}
            onClick={() => setQuando(a.valor)}
            className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
              quando === a.valor ? 'bg-tinta-900 text-white' : 'text-tinta-500 hover:bg-tinta-100'
            }`}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      {semVinculo && (
        <div className="mb-4 max-w-2xl">
          <Alerta tipo="aviso">
            Sua conta ainda nao esta ligada a um cadastro de membro. Peca ao administrador para
            fazer essa ligacao — sem ela nao da para escalar voce.
          </Alerta>
        </div>
      )}

      {!semVinculo && escalas.length === 0 && (
        <Cartao className="max-w-2xl">
          <p className="text-sm text-tinta-400">
            {quando === 'proximas'
              ? 'Você não tem escala marcada por enquanto.'
              : 'Nenhuma escala anterior por aqui.'}
          </p>
        </Cartao>
      )}

      <div className="max-w-3xl space-y-6">
        {porDia.map(([dia, doDia]) => {
          const primeira = doDia[0]!;
          return (
            <section key={dia}>
              <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-tinta-900">
                    {numeroDoDia(primeira.inicio!)}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-tinta-400">
                    {mesCurto(primeira.inicio!)}
                  </span>
                  <span className="text-sm text-tinta-400">· {diaDaSemana(primeira.inicio!)}</span>
                </div>
                <span className="text-sm text-tinta-400">{quandoEmPalavras(primeira.inicio!)}</span>
              </div>

              <div className="space-y-3 border-l-2 border-tinta-200 pl-3">
                {doDia.map((e) => (
                  <article key={e.id} className="rounded-2xl bg-white p-4 shadow-cartao">
                    <Link to={`/culto/${e.eventoId}`} className="block">
                      <h3 className="text-base font-bold uppercase tracking-wide text-tinta-900">
                        {e.titulo}
                      </h3>
                      <p className="mt-0.5 text-sm text-tinta-500">
                        {horaSP(e.inicio!)}
                        {e.local ? ` · ${e.local}` : ''} · voce em <b>{e.funcao}</b>
                      </p>

                      {e.equipe.length > 0 && (
                        <div className="mt-3 flex -space-x-2">
                          {e.equipe.slice(0, 7).map((p, i) => (
                            <span
                              key={`${p.nome}-${p.funcao}`}
                              title={`${p.nome} · ${p.funcao}`}
                              className={`flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white ${
                                CORES_AVATAR[i % CORES_AVATAR.length]
                              } ${p.status === 'indisponivel' ? 'opacity-40' : ''}`}
                            >
                              {iniciais(p.nome)}
                            </span>
                          ))}
                          {e.equipe.length > 7 && (
                            <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-tinta-100 text-[10px] font-bold text-tinta-600">
                              +{e.equipe.length - 7}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-tinta-500">
                        <span
                          className={`inline-flex items-center gap-1 font-semibold ${
                            e.confirmados < e.total ? 'text-amber-600' : 'text-tinta-900'
                          }`}
                        >
                          👍 {e.confirmados}/{e.total}
                        </span>
                        <span className="inline-flex items-center gap-1">♪ {e.musicas}</span>
                        {e.ministerio && (
                          <span className="rounded-md bg-tinta-100 px-2 py-0.5">{e.ministerio}</span>
                        )}
                        <span
                          className={`ml-auto rounded-md px-2 py-0.5 font-semibold ${
                            e.status === 'confirmado'
                              ? 'bg-tinta-900 text-white'
                              : e.status === 'indisponivel'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {e.status === 'confirmado'
                            ? 'confirmado'
                            : e.status === 'indisponivel'
                              ? 'não posso'
                              : 'responder'}
                        </span>
                      </div>
                    </Link>

                    {quando === 'proximas' &&
                      (justificando === e.id ? (
                        <div className="mt-3 space-y-2 border-t border-tinta-200 pt-3">
                          <input
                            autoFocus
                            value={justificativa}
                            onChange={(ev) => setJustificativa(ev.target.value)}
                            placeholder="Conte o motivo (opcional)"
                            className="w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                          />
                          <div className="flex gap-2">
                            <Botao
                              carregando={respondendo === e.id}
                              onClick={() =>
                                void responder(e.id, 'indisponivel', justificativa.trim() || undefined)
                              }
                            >
                              Avisar que nao posso
                            </Botao>
                            <Botao variante="secundario" onClick={() => setJustificando(null)}>
                              Cancelar
                            </Botao>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2 border-t border-tinta-200 pt-3">
                          {e.status !== 'confirmado' && (
                            <Botao
                              carregando={respondendo === e.id}
                              onClick={() => void responder(e.id, 'confirmado')}
                            >
                              Confirmar presenca
                            </Botao>
                          )}
                          {e.status !== 'indisponivel' && (
                            <Botao
                              variante="secundario"
                              onClick={() => {
                                setJustificando(e.id);
                                setJustificativa('');
                              }}
                            >
                              Nao vou poder
                            </Botao>
                          )}
                          <Link
                            to={`/culto/${e.eventoId}`}
                            className="ml-auto self-center text-sm font-semibold text-tinta-500 hover:text-tinta-900"
                          >
                            abrir culto →
                          </Link>
                        </div>
                      ))}

                    {e.justificativa && (
                      <p className="mt-2 text-xs text-tinta-400">Voce avisou: {e.justificativa}</p>
                    )}
                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </Layout>
  );
}
